import type { PlayerState } from "./types";

/**
 * Declarative achievements/badges engine. A badge is defined with a PREDICATE over
 * the player's state. They are evaluated after each advance; the newly met ones are
 * granted only once. Composable predicates → no hard-coded logic.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

export type BadgePredicate = (state: PlayerState) => boolean;

export interface BadgeDef {
  id: string;
  /** i18n key or text; the core does not render. */
  title: string;
  description?: string;
  when: BadgePredicate;
  /** Bonus XP on earning it (optional). */
  bonusXp?: number;
}

// ── Composable predicates ────────────────────────────────────────────────

/** The `key` counter reaches `n`. */
export function counterAtLeast(key: string, n: number): BadgePredicate {
  return (s) => (s.counters[key] ?? 0) >= n;
}

/** The streak of line `line` reaches `n`. */
export function streakAtLeast(line: string, n: number): BadgePredicate {
  return (s) => (s.streaks[line]?.best ?? 0) >= n || (s.streaks[line]?.current ?? 0) >= n;
}

/** The level reaches `n`. */
export function levelAtLeast(n: number): BadgePredicate {
  return (s) => s.level >= n;
}

export function allOf(...preds: BadgePredicate[]): BadgePredicate {
  return (s) => preds.every((p) => p(s));
}

export function anyOf(...preds: BadgePredicate[]): BadgePredicate {
  return (s) => preds.some((p) => p(s));
}

/**
 * Returns the newly earned badges (met now and not already present
 * in `state.badges`). Does NOT mutate the state.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function newlyEarnedBadges(state: PlayerState, defs: readonly BadgeDef[]): BadgeDef[] {
  const owned = new Set(state.badges);
  const earned: BadgeDef[] = [];
  for (const def of defs) {
    if (!owned.has(def.id) && def.when(state)) {
      earned.push(def);
    }
  }
  return earned;
}
